import { ReactElement, useState } from "react"
import dynamic from "next/dynamic"
import { LocationSummary } from "@/types/place"
import { UserRef } from "@/types/user"

import { useAppDispatch } from "@/lib/storage/hooks"
import { fetchPublicUser } from "@/lib/storage/public-user-thunks"
import {
  HoverCard,
  HoverCardContent,
  HoverCardContentProps,
  HoverCardTrigger,
} from "./ui/hover-card"
import { UserHoverCardSkeleton } from "./user-hover-card-skeleton"

// Defers react-markdown and the follow/profile controls out of `_app`. UserAvatar
// pulls this in, and UserAvatar is reachable from the notification toast in the
// store, so anything imported eagerly here lands in every route's first load.
// The popup unmounts while closed, so the body only loads on first hover.
const UserHoverCardBody = dynamic(
  () => import("./user-hover-card-body").then((m) => m.UserHoverCardBody),
  { ssr: false, loading: () => <UserHoverCardSkeleton /> }
)

interface UserHoverCardProps extends HoverCardContentProps {
  userRef: UserRef
  children: ReactElement
}

export function UserHoverCard({
  userRef,
  side = "top",
  children,
  ...props
}: UserHoverCardProps) {
  const dispatch = useAppDispatch()
  const [location, setLocation] = useState<LocationSummary>()

  const fetchUser = async () => {
    try {
      const ret = await dispatch(fetchPublicUser({ userRef })).unwrap()
      setLocation(ret.location)
    } catch (error) {
      console.error("Error fetching hover card user", userRef, error)
    }
  }

  return (
    <HoverCard onOpenChange={(open) => open && fetchUser()}>
      <HoverCardTrigger render={children} />
      <HoverCardContent {...props} side={side} size="md" className="p-4">
        <UserHoverCardBody userRef={userRef} location={location} />
      </HoverCardContent>
    </HoverCard>
  )
}
